// Campos personalizados (§ 1.9): metadatos configurables por módulo que se
// convierten en FieldDef para renderizarse en formularios/fichas.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api.ts";
import type { FieldDef, FieldType } from "../modules/registry.tsx";

export interface CustomFieldRow {
  id: string;
  module: string;
  label: string;
  apiName: string;
  fieldType: string;
  options: string[];
  section: string | null;
  isActive: boolean;
  sortOrder: number;
}

const TYPE_MAP: Record<string, FieldType> = {
  text: "text", textarea: "textarea", integer: "int", decimal: "decimal",
  currency: "currency", percent: "percent", date: "date", boolean: "bool", picklist: "enum", url: "url", email: "email",
};

function toFieldDef(f: CustomFieldRow): FieldDef {
  const type = TYPE_MAP[f.fieldType] ?? "text";
  const def: FieldDef = { name: f.apiName, label: f.label, type, custom: true, section: f.section ?? "Otros" };
  if (type === "enum") def.options = f.options ?? [];
  return def;
}

/** FieldDef[] de los campos personalizados ACTIVOS de un módulo. */
export function useCustomFields(module: string) {
  return useQuery<FieldDef[]>({
    queryKey: ["custom-fields", module],
    queryFn: async () => {
      const r = await api.get<{ rows: CustomFieldRow[] }>(`/custom-fields?module=${module}`);
      return r.rows.filter((f) => f.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(toFieldDef);
    },
    staleTime: 60_000,
  });
}

/** Aplica a los campos estándar que declaren `optionsFromCustomField` las opciones
 *  del campo personalizado indicado (p. ej. Ciudad reutiliza la lista de Región). */
export function applyDynamicOptions(fields: FieldDef[], custom: FieldDef[]): FieldDef[] {
  return fields.map((f) => {
    if (!f.optionsFromCustomField) return f;
    const src = custom.find((c) => c.name === f.optionsFromCustomField);
    if (!src?.options || src.options.length === 0) return f;
    return { ...f, type: "enum", options: src.options };
  });
}

/** Todos (incluidos inactivos) para el panel de configuración. */
export function useAllCustomFields(module: string) {
  return useQuery<{ rows: CustomFieldRow[] }>({
    queryKey: ["custom-fields-all", module],
    queryFn: () => api.get(`/custom-fields?module=${module}`),
    staleTime: 30_000,
  });
}
